<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAppToast } from '@/shared/lib/useAppToast'

import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { usePagination } from '@/shared/composables/usePagination'

import DiscountFormDialog from '../components/DiscountFormDialog.vue'

import {
  getAllDiscounts,
  createDiscount,
  updateDiscount,
  deleteDiscount
} from '../api/discounts.service'

import { getAllUsers } from '@/modules/admin/users/api/users.service'
import { getAllBrands } from '@/modules/admin/brands/api/brands.service'

const router = useRouter()
const toast = useAppToast()

const discounts = ref([])
const users = ref([])
const brands = ref([])
const b2bUsers = ref([])

const loading = ref(false)
const search = ref('')

const formVisible = ref(false)
const formMode = ref('create')
const selectedDiscount = ref(null)

const submittingDiscount = ref(false)
const deletingDiscountId = ref(null)
const discountFieldErrors = ref({})

async function loadData() {
  try {
    loading.value = true

    const [discountsData, usersData, brandsData] = await Promise.all([
      getAllDiscounts(),
      getAllUsers(),
      getAllBrands()
    ])

    discounts.value = Array.isArray(discountsData) ? discountsData : []
    users.value = Array.isArray(usersData) ? usersData : []
    brands.value = Array.isArray(brandsData) ? brandsData : []

    b2bUsers.value = users.value.filter(user =>
      user.clientType?.toUpperCase() === 'B2B'
    )
  } catch (error) {
    discounts.value = []
    users.value = []
    brands.value = []
    b2bUsers.value = []
    toast.error(error.message || 'Не удалось загрузить скидки')
  } finally {
    loading.value = false
  }
}

function getUserEmail(userId) {
  return users.value.find(user => user.id === userId)?.email || '—'
}

function getBrandName(brandId) {
  return brands.value.find(brand => brand.id === brandId)?.name || '—'
}

function goBack() {
  router.push('/admin')
}

function openCreateDialog() {
  selectedDiscount.value = null
  formMode.value = 'create'
  formVisible.value = true
  discountFieldErrors.value = {}
}

function openEditDialog(discount) {
  selectedDiscount.value = { ...discount }
  formMode.value = 'edit'
  formVisible.value = true
  discountFieldErrors.value = {}
}

function closeFormDialog() {
  formVisible.value = false
  selectedDiscount.value = null
  discountFieldErrors.value = {}
}

async function handleSubmit(formData) {
  try {
    submittingDiscount.value = true
    discountFieldErrors.value = {}

    if (formMode.value === 'create') {
      await createDiscount(formData)
      toast.success('Скидка создана')
    } else if (formMode.value === 'edit' && selectedDiscount.value?.id) {
      await updateDiscount(selectedDiscount.value.id, formData)
      toast.success('Скидка обновлена')
    }

    closeFormDialog()
    await loadData()
  } catch (error) {
    discountFieldErrors.value = error.fieldErrors || {}
    toast.error(error.message || 'Ошибка при сохранении скидки')
  } finally {
    submittingDiscount.value = false
  }
}

async function handleDelete(discount) {
  if (deletingDiscountId.value != null) return
  try {
    if (!discount?.id) return

    deletingDiscountId.value = discount.id

    await deleteDiscount(discount.id)
    toast.success('Скидка удалена')

    await loadData()
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении скидки')
  } finally {
    deletingDiscountId.value = null
  }
}

const filteredDiscounts = computed(() => {
  const searchText = search.value.trim().toLowerCase()

  return discounts.value.filter(discount => {
    const email = getUserEmail(discount.userId).toLowerCase()
    const brand = discount.brandId
      ? getBrandName(discount.brandId).toLowerCase()
      : 'все бренды'

    return (
      !searchText ||
      email.includes(searchText) ||
      brand.includes(searchText)
    )
  })
})

onMounted(loadData)

const { currentPage, totalPages, paginatedItems: paginatedDiscounts } = usePagination(filteredDiscounts)
</script>

<template>
  <div class="admin-discounts-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <Button
          label="Создать скидку"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateDialog"
          :disabled="b2bUsers.length === 0"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Скидки</h1>
          <p>Управление скидками пользователей по брендам</p>
        </div>
      </div>

      <div class="filters">
        <div class="filter-group filter-group--search">
          <label>Поиск</label>
          <InputText
            v-model="search"
            placeholder="Поиск по email пользователя или бренду"
            class="filter-control"
          />
        </div>
      </div>

      <Card class="discounts-card">
        <template #content>
          <div v-if="loading" class="state-block">
            Загрузка...
          </div>

          <div v-else-if="discounts.length === 0" class="state-block">
            Список скидок пуст
          </div>

          <div v-else-if="filteredDiscounts.length === 0" class="state-block">
            По вашему запросу ничего не найдено
          </div>

          <div v-else class="discounts-grid">
            <div
              v-for="discount in paginatedDiscounts"
              :key="discount.id"
              class="discount-card"
            >
              <div class="discount-header">
                <div class="discount-user">
                  {{ getUserEmail(discount.userId) }}
                </div>
              </div>

              <div class="discount-description">
                <div class="discount-row">
                  <span class="discount-label">Бренд</span>
                  <span class="discount-value">
                    {{ discount.brandId ? getBrandName(discount.brandId) : 'Все бренды' }}
                  </span>
                </div>

                <div class="discount-row">
                  <span class="discount-label">Скидка</span>
                  <span class="discount-value">
                    {{ discount.discountPercent }}%
                  </span>
                </div>
              </div>

              <div class="discount-meta">
                <span class="meta-chip">
                  {{ discount.isActive ? 'Активна' : 'Неактивна' }}
                </span>

                <span class="meta-chip">
                  {{ discount.brandId ? 'По бренду' : 'Глобальная' }}
                </span>
              </div>

              <div class="discount-actions">
                <Button
                  icon="pi pi-pencil"
                  class="icon-btn"
                  outlined
                  title="Редактировать"
                  @click="openEditDialog(discount)"
                />

                <Button
                  icon="pi pi-trash"
                  class="icon-btn icon-btn--danger"
                  outlined
                  title="Удалить"
                  :loading="deletingDiscountId === discount.id"
                  :disabled="deletingDiscountId === discount.id"
                  @click="handleDelete(discount)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <DiscountFormDialog
        :visible="formVisible"
        :mode="formMode"
        :discount="selectedDiscount"
        :users="b2bUsers"
        :brands="brands"
        :submitting="submittingDiscount"
        :field-errors="discountFieldErrors"
        @close="closeFormDialog"
        @submit="handleSubmit"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-discounts-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.filter-control {
  width: 100%;
}

.discounts-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.discounts-card.p-card) {
  background: #ffffff;
}

:deep(.discounts-card .p-card-content) {
  padding: 24px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.discounts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.discount-card {
  background: #ffffff;
  border: 1px solid #ececec;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 10px 24px rgba(17, 17, 17, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.discount-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(17, 17, 17, 0.08);
  border-color: #dddddd;
}

.discount-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.discount-user {
  font-weight: 700;
  font-size: 17px;
  color: #111111;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.discount-description {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.discount-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8f8f8;
  border: 1px solid #efefef;
}

.discount-label {
  font-size: 13px;
  color: #6b6b6b;
}

.discount-value {
  font-size: 14px;
  font-weight: 600;
  color: #111111;
  text-align: right;
  word-break: break-word;
}

.discount-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #555555;
  font-size: 12px;
  font-weight: 500;
}

.discount-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.create-btn.p-button:disabled) {
  background: #d9d9d9;
  border-color: #d9d9d9;
  color: #7a7a7a;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

:deep(.p-inputtext) {
  border-radius: 14px;
  border-color: #ddd;
}

:deep(.p-inputtext:focus) {
  border-color: #111;
  box-shadow: 0 0 0 2px rgba(196, 18, 23, 0.08);
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .discounts-grid {
    grid-template-columns: 1fr;
  }

  .discount-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .discount-value {
    text-align: left;
  }
}

@media (max-width: 480px) {
  .page-shell {
    padding: 16px 12px 32px;
  }

  .page-header h1 {
    font-size: 26px;
  }

  .discount-card {
    padding: 14px;
  }

  .discount-user {
    font-size: 15px;
  }
}
</style>